import React, { useEffect, useState } from "react";
import { Modal, Descriptions, Empty, Tag, Space, Divider, Typography, Tooltip } from "antd";
import { ClockCircleOutlined, GlobalOutlined, WarningOutlined } from "@ant-design/icons";
import { useTimeZone, TIMEZONE_OPTIONS } from "../../../../../contexts/TimeZoneContext";
import AircraftTag from "../../../../../components/AircraftTag";

const { Title, Text } = Typography;

// 星期映射表
const WEEKDAY_MAP = {
    MON: "周一",
    TUE: "周二",
    WED: "周三",
    THU: "周四",
    FRI: "周五",
    SAT: "周六",
    SUN: "周日",
};

/**
 * 宵禁规则查看组件
 * @param {Object} props
 * @param {boolean} props.visible - 是否显示对话框
 * @param {Function} props.onClose - 关闭对话框回调
 * @param {Object} props.airport - 机场信息对象
 */
const CurfewRulesViewer = ({ visible, onClose, airport }) => {
    const { timeZone } = useTimeZone(); // 使用时区上下文
    const [convertError, setConvertError] = useState(null);

    if (!airport) {
        return null;
    }

    const { iata_code, airport_name, timezone, dep_curfew_rules = [], arr_curfew_rules = [] } = airport;
    const hasDepRules = dep_curfew_rules && dep_curfew_rules.length > 0;
    const hasArrRules = arr_curfew_rules && arr_curfew_rules.length > 0;
    const hasNoRules = !hasDepRules && !hasArrRules;

    // 格式化适用时间
    const formatApplyTime = (rule) => {
        const type = rule.type || "daily";

        if (type === "daily") {
            return "每天";
        } else if (type === "weekly") {
            if (!rule.weekdays || rule.weekdays.length === 0) {
                return "每周（未指定天数）";
            }
            return rule.weekdays.map((day) => WEEKDAY_MAP[day] || day).join("、");
        } else if (type === "specific") {
            if (!rule.date_start || !rule.date_end) {
                return "指定日期（未设置）";
            }
            return `${rule.date_start} 至 ${rule.date_end}`;
        }
        return "未知类型";
    };

    // 获取当前使用的时区标识
    const getTimezoneLabel = () => {
        switch (timeZone) {
            case TIMEZONE_OPTIONS.BEIJING:
                return "UTC+8";
            case TIMEZONE_OPTIONS.LOCAL:
                return iata_code;
            default:
                return "UTC";
        }
    };

    // 获取当前使用的时区描述
    const getTimezoneInfo = () => {
        switch (timeZone) {
            case TIMEZONE_OPTIONS.BEIJING:
                return "北京时间 (UTC+8)";
            case TIMEZONE_OPTIONS.LOCAL:
                return `${iata_code}当地时间`;
            default:
                return "世界标准时间(UTC)";
        }
    };

    // 检查是否有无效的宵禁规则
    const hasInvalidRules = () => {
        const checkRules = (rules) => {
            if (!Array.isArray(rules)) return false;
            return rules.some((rule) => !rule.start || !rule.end);
        };

        return checkRules(dep_curfew_rules) || checkRules(arr_curfew_rules);
    };

    return (
        <Modal
            title={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>{`${airport_name || iata_code} (${iata_code}) 宵禁规则`}</span>
                    {timeZone !== TIMEZONE_OPTIONS.UTC && (
                        <Tooltip title={getTimezoneInfo()}>
                            <Tag icon={<GlobalOutlined />} color="blue">
                                {getTimezoneLabel()}
                            </Tag>
                        </Tooltip>
                    )}
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={600}
            className="curfew-rules__viewer"
        >
            {hasInvalidRules() && (
                <div style={{ marginBottom: 16 }}>
                    <Tag icon={<WarningOutlined />} color="warning">
                        部分宵禁规则数据可能不完整，显示可能不准确
                    </Tag>
                </div>
            )}

            {hasNoRules ? (
                <Empty description="该机场没有设置宵禁规则" />
            ) : (
                <div>
                    {hasDepRules && (
                        <div className="rule-section">
                            <Title level={5} className="curfew-rules__title">
                                <ClockCircleOutlined style={{ color: "#f5222d" }} />
                                <span style={{ marginLeft: 8 }}>起飞宵禁规则</span>
                            </Title>

                            {dep_curfew_rules.map((rule, index) => (
                                <div key={`dep-${index}`} style={{ marginBottom: 16 }}>
                                    <Descriptions bordered size="small" column={1}>
                                        <Descriptions.Item label="适用时间(UTC)">
                                            <Tag color="cyan">{formatApplyTime(rule)}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="宵禁时段(UTC)">
                                            <Tag color="red">
                                                {rule.start} 至 {rule.end}
                                            </Tag>
                                        </Descriptions.Item>
                                        {rule.exceptions && rule.exceptions.length > 0 && (
                                            <Descriptions.Item label="例外飞机">
                                                <Space wrap>
                                                    {rule.exceptions.map((reg) => (
                                                        <AircraftTag key={reg} registration={reg} />
                                                    ))}
                                                </Space>
                                            </Descriptions.Item>
                                        )}
                                    </Descriptions>
                                </div>
                            ))}
                        </div>
                    )}

                    {hasDepRules && hasArrRules && <Divider />}

                    {hasArrRules && (
                        <div className="rule-section">
                            <Title level={5} className="curfew-rules__title">
                                <ClockCircleOutlined style={{ color: "#52c41a" }} />
                                <span style={{ marginLeft: 8 }}>降落宵禁规则</span>
                            </Title>

                            {arr_curfew_rules.map((rule, index) => (
                                <div key={`arr-${index}`} style={{ marginBottom: 16 }}>
                                    <Descriptions bordered size="small" column={1}>
                                        <Descriptions.Item label="适用时间(UTC)">
                                            <Tag color="cyan">{formatApplyTime(rule)}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="宵禁时段(UTC)">
                                            <Tag color="red">
                                                {rule.start} 至 {rule.end}
                                            </Tag>
                                        </Descriptions.Item>
                                        {rule.exceptions && rule.exceptions.length > 0 && (
                                            <Descriptions.Item label="例外飞机">
                                                <Space wrap>
                                                    {rule.exceptions.map((reg) => (
                                                        <AircraftTag key={reg} registration={reg} />
                                                    ))}
                                                </Space>
                                            </Descriptions.Item>
                                        )}
                                    </Descriptions>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
};

export default CurfewRulesViewer;
